import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  TUpdateReplyTemplateValidation,
  UpdateReplyTemplateValidationSchema,
  useUpdateReplyTemplateMutation,
} from '../../../api/update-reply-template.api';
import { GET_REPLY_TEMPLATES_QUERY_KEY } from '../../../api/get-reply-templates.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useCustomToast } from '@/hooks/use-custom-toast';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';
import { Input } from '@/components/_ui/input';
import { cn } from '@/utils/cn';

export type TUpdateReplyTemplateModalType = 'UpdateReplyTemplateModal';
export interface IUpdateReplyTemplateModalProps {
  replyTemplateId: string;
  templateName: string;
  templateContent: string;
}

const UpdateReplyTemplateModal = memo(
  ({ replyTemplateId, templateName, templateContent }: IUpdateReplyTemplateModalProps) => {
    const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
    const showToast = useCustomToast();

    const isOpenUpdateReplyTemplateModal = useMemo(() => {
      return caseModuleModalRootActions.checkIsOpenModal('UpdateReplyTemplateModal');
    }, [caseModuleModalRootActions]);

    const form = useForm<TUpdateReplyTemplateValidation>({
      resolver: zodResolver(UpdateReplyTemplateValidationSchema),
      mode: 'onChange',
      defaultValues: {
        templateId: '',
        title: '',
        content: '',
      },
    });

    useEffect(() => {
      if (replyTemplateId || templateName !== undefined || templateContent !== undefined) {
        form.reset({
          templateId: replyTemplateId || '',
          title: templateName || '',
          content: templateContent || '',
        });
      }
    }, [replyTemplateId, templateName, templateContent, form]);

    const mutationUpdateReplyTemplate = useUpdateReplyTemplateMutation({
      mutationConfigOptions: {
        onError: (_error) => {
          const errorMessage = axiosExtractErrorMessage(_error) || 'Error updating reply template, please try again';
          showToast({
            message: errorMessage,
            type: 'error',
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [GET_REPLY_TEMPLATES_QUERY_KEY] });
          caseModuleModalRootActions.closeModal('UpdateReplyTemplateModal');
          form.reset();
          showToast({
            message: 'Reply template updated successfully!',
            type: 'success',
          });
        },
      },
    });

    const handleSubmitUpdateReplyTemplate = useCallback(
      (data: TUpdateReplyTemplateValidation) => {
        mutationUpdateReplyTemplate.mutate({
          templateId: data.templateId,
          title: data.title,
          content: data.content,
        });
      },
      [mutationUpdateReplyTemplate]
    );

    const handleClose = useCallback(() => {
      caseModuleModalRootActions.closeModal('UpdateReplyTemplateModal');
      form.reset();
    }, [caseModuleModalRootActions, form]);

    return (
      <Modal
        isOpen={isOpenUpdateReplyTemplateModal}
        lockBodyScroll
        onClose={handleClose}
        className="p-[32px] w-[500px]"
      >
        <ModalHeader className="border-b border-[#D9D9D9] pb-[16px] pr-0">
          <ModalTitle>Update Template</ModalTitle>
          <ModalDescription>Edit this template that agents use when responding to customers.</ModalDescription>
        </ModalHeader>
        <ModalContent className="mt-[8px]">
          <form
            onSubmit={(e) => {
              handleDomBubbleEvent(e);
              form.handleSubmit(handleSubmitUpdateReplyTemplate)(e);
            }}
            id="update-reply-template-form"
            className="grid gap-[8px]"
          >
            <div className="grid gap-[2px]">
              <label htmlFor="update-reply-template-name" className="text-[#25151E] text-[14px] font-[600]">
                Template Name
              </label>
              <Input id="update-reply-template-name" placeholder="Enter Template Name" {...form.register('title')} />
              {form.formState.errors.title && (
                <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.title.message}</p>
              )}
            </div>

            <div className="grid gap-[2px]">
              <label htmlFor="update-reply-template-content" className="text-[#25151E] text-[14px] font-[600]">
                Template Content
              </label>
              <textarea
                id="update-reply-template-content"
                placeholder="Type your auto reply messages here..."
                rows={6}
                {...form.register('content')}
                className={cn(
                  'w-full text-[14px] rounded-[8px] pl-[12px] pr-[12px] py-[10px]',
                  'border border-[#D9D9D9] text-[#4B4B4B] bg-[#ffffff]',
                  'outline-none transition-all duration-150 focus:border-[#0C8DBF]',
                  'resize-y min-h-[120px]'
                )}
              />
              {form.formState.errors.content && (
                <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.content.message}</p>
              )}
            </div>
          </form>
        </ModalContent>
        <ModalFooter>
          <ModalCloseButton
            onClick={(e) => {
              handleDomBubbleEvent(e);
              handleClose();
            }}
          >
            Cancel
          </ModalCloseButton>
          <ModalConfirmButton
            form="update-reply-template-form"
            type="submit"
            disabled={mutationUpdateReplyTemplate.isPending}
          >
            {mutationUpdateReplyTemplate.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
            Save
          </ModalConfirmButton>
        </ModalFooter>
      </Modal>
    );
  }
);
UpdateReplyTemplateModal.displayName = 'UpdateReplyTemplateModal';

export default UpdateReplyTemplateModal;
